import { useRef, useSyncExternalStore } from 'react';

import { getSnapshot, intercomMayStart, npsMayLoad, subscribe } from 'services/interruptions';

// The server renders the first state ("onboarding undecided"), the same as the browser's first
// render, so nothing differs on hydration.
const useSnapshot = () => useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

/**
 * Whether Intercom may start: at once for a logged-out visitor, and for a logged-in reader once the
 * onboarding has been settled for them. Once started it stays started, for that reader.
 */
export function useIntercomMayStart(loggedIn) {
  const snapshot = useSnapshot();
  // Once it has started for this reader it keeps running, even if the onboarding turns up late (after
  // the time limit): taking Intercom away again would only shut its messenger down. A different
  // reader (a login or logout) starts the order again.
  const startedFor = useRef(null);
  if (intercomMayStart(snapshot, loggedIn)) {
    startedFor.current = snapshot.generation;
  }
  return startedFor.current === snapshot.generation && snapshot.generation !== null;
}

/**
 * Whether the NPS survey may be loaded: not when the onboarding was shown, and not when Intercom
 * has a message.
 */
export function useNpsMayLoad() {
  const snapshot = useSnapshot();
  // Once the survey has been let in for this reader it stays in: a message from Intercom that comes
  // later must not tear it down. A different reader (a login or logout) starts again.
  const letInFor = useRef(null);
  if (npsMayLoad(snapshot)) {
    letInFor.current = snapshot.generation;
  }
  return letInFor.current === snapshot.generation;
}

/**
 * Whether the Premium offer is about to be shown or is on screen (the apps show it right after
 * signing in). The onboarding waits for it.
 */
export function usePremiumPromoPending() {
  return useSnapshot().premiumPromoPending;
}
